import type { Document } from '@contentful/rich-text-types';
import type { FormEndpointType } from 'components/Contact/FormEndpoints';
import type { Asset, Entry, ReferencedEntry } from 'contentful';
import { CookieConfig } from 'services/analytics/CookieConfig';
import type { FlatSysV2, HasContentType } from 'services/cms/typesUtil';
import { getContentType } from 'utils/CloverdotcomEntryUtil';
import type { FlatEntry } from '../../services/cms/flattenContentful';
import type { Entries, Field } from './utilTypes';
// eslint-disable-next-line import/no-unresolved
import { VideoPlayerProps } from '@clover-growth/video-player/lib/VideoPlayer/VideoPlayer.types';

export enum ContentType {
  ACCORDION_ASSEMBLY = 'accordionAssembly',
  ARTICLE_ASSEMBLY = 'articleAssembly',
  BANNER_MODULE = 'bannerComponent',
  BANNER_ASSEMBLY = 'bannerAssembly',
  BASIC_PAGE = 'page',
  BUILD_YOUR_OWN_MODULE = 'buildYourOwn',
  BUNDLE = 'bundle',
  BUNDLE_FEATURE = 'bundleFeature',
  BUNDLE_FEATURE_CATEGORY = 'bundleFeatureCategory',
  BUNDLE_FEATURE_VALUE = 'bundleFeatureValue',
  BUNDLE_SUMMARY_MODULE = 'bundleSummaryModule',
  BUSINESS_RESOURCE_GRID = 'businessResourceGrid',
  CARD_ASSEMBLY = 'cardAssembly',
  CAROUSEL_ASSEMBLY = 'carouselAssembly',
  CLIENT_CONFIG = 'clientConfig',
  COMPONENT_ASSEMBLY = 'componentAssembly',
  CONTROLLED_SCROLL_ASSEMBLY = 'controlledScrollAssembly',
  CONTROLLED_SCROLL_ITEM_ASSEMBLY = 'controlledScrollItemAssembly',
  ENTRY_SET = 'set',
  EXTERNAL_LINK = 'externalLink',
  FAQ_MODULE = 'faqModule',
  FEATURE_FLAG = 'featureFlag',
  FLOATING_TEXT_ASSEMBLY = 'floatingAssembly',
  FOOTER = 'footer',
  GRID_ASSEMBLY = 'gridAssembly',
  HERO_MODULE = 'heroModule',
  ICON_FEATURE_GRID = 'iconFeatureGrid',
  IMAGE_TEXT_MODULE = 'imageTextModule',
  INTERNAL_LINK = 'internalLink',
  LEAD_GEN_FORM_MODULE = 'leadGenFormModule',
  LEGAL_DISCLAIMER = 'legalDisclaimer',
  LINK_SET = 'linkSet',
  LIST_GALLERY_MODULE = 'listGalleryModule',
  MEDIA = 'media',
  MEDIA_MODULE = 'mediaAssembly',
  MODAL_ASSEMBLY = 'modalModule',
  MODAL_LINK = 'modalLink',
  MULTIPANEL_MODULE = 'multiPanelModule',
  NAVIGATION = 'navigation',
  PILL_TABS_MODULE = 'pillTabsModule',
  PLAIN_TEXT_MODULE = 'plainTextAssembly',
  PRICING_DETAIL_MODULE = 'pricingDetailModule',
  PRODUCT = 'product',
  PRODUCT_MODULE = 'productModule',
  PRODUCT_TILE_ASSEMBLY = 'productTileAssembly',
  QUOTES_MODULE = 'quotesAssembly',
  RESELLER = 'reseller',
  SALES_CONTACT_MODULE = 'salesContactModule',
  SINGLE_TESTIMONIAL_MODULE = 'singleTestimonialModule',
  SOFTWARE_PLAN = 'softwarePlan',
  SOLUTION = 'solution',
  SOLUTION_PICKER_MODULE = 'solutionPickerModule',
  STICKY_HEADER_ASSEMBLY = 'stickyHeaderAssembly',
  SUBJECT = 'subject',
  TABS_ASSEMBLY = 'tabsAssembly',
  TAB_ITEM = 'tabItem',
  TESTIMONIAL_MODULE = 'testimonialModule',
  VERTICAL_MARKET = 'vertical',
  SIDE_BY_SIDE_MODULE = 'sideBySideDeviceModule',
  NAV_SUB_ITEM = 'navSubItem',
  PRODUCT_SYSTEMS_CATALOG = 'productSystemsCatalog',
  BANNER_MODULE_ASSEMBLY = 'bannerModuleAssembly',
  CARD_GRID_ASSEMBLY = 'cardGridAssembly',
  HERO_MODULE_ASSEMBLY = 'heroModuleAssembly',
  ICON_GRID_ASSEMBLY = 'iconGridAssembly',
  TESTIMONIAL_ASSEMBLY = 'testimonialAssembly',
  PAYMENTS_COMPARISON_MODAL_V2 = 'paymentsComparisonModalV2',
  CLOVER_CARE_BONUS_SECTION = 'cloverCareBonusSection',
  PROMO_BANNER = 'promoBanner',
  PROMO_SUMMARY = 'promoSummary',
  DEVICES_MENU = 'devicesMenu',
  MODAL_FEATURES = 'modalFeatures',
  FEATURES = 'features',
  SUBSCRIPTION_EARLY_CANCELLATION_TERMS = 'earlyCancellationTerms',
  FEATURE_GARDEN = 'featureGarden',
  VERTICAL_SYSTEM_MODULE = 'verticalSystemModule',
  SYSTEM_DIAGRAM = 'systemDiagram',
  SHEET_ASSEMBLY = 'sheetAssembly',
  SUGGESTED_SETUP = 'suggestedSetup',
  VALUE_STATEMENT = 'valueStatement',
  FI_INTEGRATION = 'fiIntegration',
}

export function is<T>(contentType: ContentType, maybe?: Entry<any>): maybe is Entry<T>;
export function is<T>(contentType: ContentType, maybe?: ReferencedEntry<any>): maybe is ReferencedEntry<T>;
export function is<T>(
  contentType: ContentType,
  maybe?: Entry<any> | ReferencedEntry<any>,
): maybe is Entry<T> | ReferencedEntry<T> {
  return getContentType(maybe) === contentType;
}

export function as<T>(contentType: ContentType, entry?: Entry<any>): Entry<T> | undefined;
export function as<T>(contentType: ContentType, entry?: ReferencedEntry<any>): ReferencedEntry<T> | undefined;
export function as<T>(
  contentType: ContentType,
  entry?: Entry<any> | ReferencedEntry<any>,
): Entry<T> | ReferencedEntry<T> | undefined {
  return (is<T>(contentType, entry) && entry) || undefined;
}

// ******** special attribute enums ******** //

export enum Breakpoint {
  MOBILE = 'Mobile',
  TABLET = 'Tablet',
  DESKTOP = 'Desktop',
}

export enum CarouselType {
  FEATURE = 'Feature',
  SQUARE = 'Square',
  HERO = 'Hero',
  PRODUCT = 'Product',
  ACCORDION = 'Accordion',
  ACCORDION_CASE_STUDY = 'CaseStudy',
  DEVICE = 'Device',
}

export enum DynamicType {
  SHOPPING_FOOTER = 'shopping-footer',
}

export enum NavigationType {
  FOOTER = 'footer',
  HEADER = 'header',
  HEADER_V2 = 'header-v2',
  HEADER_UTIL = 'header-util',
  HEADER_UTIL_V2 = 'header-util-v2',
  SIDEBAR = 'sidebar',
  SIDEBAR_SALES_HELP = 'sidebar-sales-help',
  SIDEBAR_OVERRIDE = 'sidebar-override',
}

export enum ImageAlignment {
  LEFT = 'Left',
  RIGHT = 'Right',
}

export enum ListGalleryVariant {
  STATIC = 'Static',
  STICKY_SCROLL = 'StickyScroll',
}

export enum ImageMobileAlignment {
  TOP = 'Top',
  BOTTOM = 'Bottom',
}

export enum TextAlignment {
  LEFT = 'Left',
  CENTER = 'Center',
  RIGHT = 'Right',
  TOP = 'Top',
}

export enum TextPosition {
  LEFT = 'Left',
  RIGHT = 'Right',
}

export enum TextMediaModuleType {
  HEADLINE = 'Headline',
  HERO = 'Hero',
}

export enum TextMediaHeroType {
  FULLBLEED = 'Fullbleed',
  HARDWARE = 'Hardware',
  COLLAGE = 'Collage',
  CTA = 'CTA',
}

/* this enum can be used in the future to add other media source types(like youtube, contentful asset) */
export enum MediaSourceType {
  VIMEO = 'Vimeo',
}

export enum ImageSourceType {
  JSON = 'application/json',
}

export enum CardLayoutType {
  GRID = 'Grid',
  CAROUSEL = 'Carousel',
}

export enum ObjectFit {
  CONTAIN = 'contain',
  COVER = 'cover',
}

export enum Weight {
  LIGHT = 'Light',
  REGULAR = 'Regular',
  BOLD = 'Bold',
}

export enum ContentfulPadding {
  NONE = 'None',
  SMALL = 'Small',
  MEDIUM = 'Medium',
  LARGE = 'Large',
}

export enum ContentfulMargin {
  NONE = 'None',
}

export enum LinkVariant {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  TEXT = 'text',
  ARROW_TEXT = 'arrow-text',
}

export enum HeroCtaVariant {
  WHITE = 'White',
  DARK_GREEN = 'Dark Green',
}

export enum HeroImageVariant {
  HERO = 'Hero',
  GALLERY = 'Gallery',
  DEVICE = 'Device',
  FULLBLEED = 'Fullbleed',
}

export enum VerticalPosition {
  TOP = 'Top',
  MIDDLE = 'Middle',
  BOTTOM = 'Bottom',
}

export enum BundleFeatureValueVariant {
  Included = 'Included',
  Image = 'Image',
  OnlyText = 'Only Text',
  Product = 'Product',
  Pricing = 'Pricing',
  Link = 'Link',
}

export enum CardsArrangement {
  WITH_SPACE = 'With Space',
  WITHOUT_SPACE = 'Without Space',
}

export enum PricingTableFeatureType {
  PRODUCT_MODAL = 'Product Modal',
  LINK = 'Link',
}

export enum Theme {
  WHITE = 'White',
  DARK_GREEN = 'Dark Green',
  LIGHT_GREEN = 'Light Green',
  GREEN = 'Green',
}

export enum ButtonTheme {
  LIGHT = 'light',
  GREEN = 'green',
  PURPLE = 'purple',
  IMAGE = 'image',
}

export enum TestimonialType {
  NO_IMAGE = 'NoImage',
  WITH_IMAGE = 'WithImage',
  SHOWCASE = 'Showcase',
}

export enum ColorTheme {
  GREEN = 'Green',
  PURPLE = 'Purple',
}

export enum CustomIconPosition {
  DEFAULT = 'default',
  BEFORE = 'before',
  AFTER = 'after',
}

export enum CardModuleVariations {
  DEFAULT = 'default',
  FEATURE_TILE = 'feature tile',
}

// ******** common fields ******** //

export type HasBackgroundColor = Field<{ backgroundColor: string }>;

export type HasColor = Field<{ color: string }>;

export type HasSecondaryFontColor = Field<{ secondaryFontColor: string }>;

export type HasSlug = Field<{ slug: string }>;

export type HasTitle = Field<{ title: string; titleColor: string }>;

export type HasTitleMaxWidth = Field<{ title: string; titleMaxWidth: string }>;

export type HasSubTitle = Field<{ subTitle: string; subTitleColor: string }>;

export type HasInternalName = Field<{ internalName: string }>;

export type HasBlurb = Field<{ blurb: string; blurbColor: string }>;

export type HasTextAlignment = Field<{ textAlignment: TextAlignment }>;

export type HasImage = Field<{ image: Asset }>;

export type HasModalFeatures = Field<{ modalFeatures: FlatEntry<ModalFeatures> }>;

export type HasFeatures = Field<{ features: FlatEntry<Features>[] }>;

export type HasImagePosition = Field<{ imagePosition: string }>;

export type HasImageFit = Field<{ imageFit: ObjectFit }>;

export type HasCustomIcon = Field<{ customIcon: Asset }>;

export type HasCustomIconPosition = Field<{ customIconPosition: CustomIconPosition }>;

export type HasImages = Field<{ images: Asset[] }>;

export type HasMobileImage = Field<{ mobileImage: Asset }>;

export type HasTabletImage = Field<{ tabletImage: Asset }>;

export type HasVideo = Field<{ video: Asset }> & HasImage;

export type HasLink = Field<{ link: ReferencedEntry<Link> }>;

export type HasSecondaryLinks = Field<{ secondaryLinks: ReadonlyArray<ReferencedEntry<Link>> }>;

export type HasCtaButtonOption = Field<{ isCtaButton: boolean }>;

export type HasCarouselType = Field<{ carouselType: CarouselType }>;

export type HasCustomIconAppearsIn = Field<{ customIconAppearsIn: ReadonlyArray<Breakpoint> }>;

export type HasMaterialIcon = Field<{ materialIcon: string }>;

export type HasModuleType = Field<{ moduleType: TextMediaModuleType }>;

export type HasHeroType = Field<{ heroType: TextMediaHeroType }>;

export type HasTextBoxAlignment = Field<{ textBoxAlignment: TextAlignment.LEFT | TextAlignment.RIGHT }>;

export type HasExternalSources = Field<{ youtubeId?: string }>;

export type HasAmbient = Field<{ isAmbient?: boolean }>;

export type HasMedia = Field<{ media?: ReferencedEntry<MediaFields> }>;

export type HasDisallowedResellers = Field<{ disallowedResellers?: ReadonlyArray<string> }>;

export type HasLinkVariant = Field<{ variant?: LinkVariant }>;

export type FontWeight = Weight.LIGHT | Weight.REGULAR | Weight.BOLD;

export type HasDescription = Field<{ description: string }>;

export type HasDcsId = Field<{ dcsId: string }>;

export type HasKey = Field<{ key: string }>;

export type HasDcsEntities = Field<{ dcsEntities?: ReferencedEntry<DcsEntities> }>;

export type HasButtonTheme = Field<{ buttonTheme?: ButtonTheme }>;

export type HasButtonOutline = Field<{ buttonOutline?: boolean }>;

export type HasHint = Field<{ hint?: string }>;

export type HasComponentMetadata = Field<{
  headingTagName?: keyof React.ReactHTML;
}>;

export type HasVideoPlayer = Field<{ videoPlayer: ReferencedEntry<VideoPlayer> }>;

export type HasRichBlurb = Field<{ richBlurb?: Document }>;

// ******** org ******** //

export type Topic = Product | Subject;

export type Link = InternalLink | ExternalLink | ModalLink | Page | Product | NavSubItem;

export type Assembly = HasComponentMetadata &
  AccordionAssembly &
  CarouselAssembly &
  BannerAssembly &
  CardAssembly &
  ControlledScrollAssembly &
  FaqModule &
  FloatingTextAssembly &
  GridAssembly &
  MultiPanelModule &
  ProductTileAssembly &
  TabsAssembly &
  QuotesAssembly &
  TestimonialAssembly &
  SolutionPickerModule &
  StickyHeaderAssembly &
  ProductSystemsCatalog &
  SideBySideAssembly &
  HeroModule &
  FeatureGardenModule;

// ******** topic content types ******** //

export type Subject = HasInternalName &
  HasBlurb &
  HasRichBlurb &
  HasTitle &
  HasTitleMaxWidth &
  HasSubTitle &
  HasImage &
  HasImagePosition &
  HasImageFit &
  HasMobileImage &
  HasTabletImage &
  HasVideo &
  HasLink &
  HasSecondaryLinks &
  HasExternalSources &
  HasAmbient &
  HasMedia &
  HasBackgroundColor &
  HasDcsEntities &
  HasVideoPlayer;

export type Image = HasInternalName &
  HasImage &
  HasImagePosition &
  HasImageFit &
  HasMobileImage &
  HasTabletImage;

export type SystemDiagram = HasComponentMetadata &
  HasInternalName &
  HasTitle & {
    systemOverviewTabLabel: string;
    softwarePlanTabLabel: string;
    item?: ReferencedEntry<Subject>;
  };

export type FiIntegration = HasInternalName &
  HasLink & {
    logo: ReferencedEntry<Subject>;
    items?: Entries<Subject>;
    checkboxLabel: string;
    feesInformation: string;
  };

export type SuggestedSetup = HasComponentMetadata &
  HasInternalName &
  Field<{
    topic: ReferencedEntry<Subject>;
    textPosition: TextPosition;
    suggestionTitle: string;
    suggestionValue: string;
    theme: ColorTheme;
  }>;

export type ValueStatement = HasComponentMetadata &
  HasInternalName &
  HasRichBlurb &
  Field<{
    items: Entries<Subject>;
    enableModal: boolean;
  }>;

export type VideoPlayer = HasInternalName &
  Field<VideoPlayerProps> &
  Field<{ thumbnail: Asset; media: Asset }>;

export type VerticalMarket = HasTitle &
  HasImage &
  HasVideo &
  Field<{
    primaryColor: string;
    accentColor: string;
  }>;

export type Faq = HasInternalName &
  HasRichBlurb &
  Field<{
    question: string;
    answer: string;
  }>;

export enum FaqVariantOptions {
  CIRCLE_ICON = 'CircleIcon',
  ARROW_ICON = 'ArrowIcon',
}

export type Product = HasDcsId &
  Subject &
  Field<{
    firstDataMarketplaceId: string;
    msrp?: number;
  }>;

export type DcsEntities = HasInternalName &
  Field<{
    bundleId: string;
    productId: string;
    softwarePlanId: string;
  }>;

export type ModalFeatures = HasInternalName &
  HasFeatures &
  Field<{
    headlineTitle: string;
    secondaryTitle: string;
    price: FlatEntry<Subject>;
    blurb: FlatEntry<Subject>;
  }>;

export type Features = HasInternalName &
  Field<{
    title: string;
    blurb: ReferencedEntry<Subject>;
  }>;

export type ExternalLink = HasTitle &
  HasMaterialIcon &
  HasDisallowedResellers &
  HasLinkVariant &
  HasImage &
  HasCustomIcon &
  HasCustomIconPosition &
  HasCustomIconAppearsIn &
  HasButtonTheme &
  HasButtonOutline &
  HasHint &
  Field<{
    url: string;
    queryParameters: any;
    newTab: boolean;
  }>;

export type InternalLink = HasTitle &
  HasMaterialIcon &
  HasDisallowedResellers &
  HasLinkVariant &
  HasImage &
  HasCustomIcon &
  HasCustomIconPosition &
  HasCustomIconAppearsIn &
  HasButtonTheme &
  HasButtonOutline &
  HasHint &
  Field<{
    page: ReferencedEntry<Page>;
    queryParameters: any;
  }>;

export type ModalLink = HasTitle &
  HasLinkVariant &
  Field<{
    modal: ReferencedEntry<ModalAssembly>;
    hideButton: boolean;
  }>;

export type TabItem = HasTitle &
  HasInternalName &
  Field<{
    module: ReferencedEntry<MediaAssembly | CardAssembly | AccordionAssembly>;
  }>;

export type NavSubItem = HasTitle &
  HasBlurb &
  HasInternalName &
  HasLink &
  Field<{
    subItemLinks: Entries<HasLink>;
  }>;

export type VerticalSystemModule = HasInternalName &
  HasTitle &
  Field<{
    items: Entries<VerticalSystemItem>;
    item1: string;
    item1Icon: Asset;
    item2: string;
    item2Icon: Asset;
    item3: string;
    item3Icon: Asset;
    item4: string;
    item4Icon: Asset;
  }>;

export type VerticalSystemItem = HasInternalName &
  HasTitle &
  HasLink &
  Field<{
    item1Title: string;
    item1Description: string;
    item1Link?: Entry<ExternalLink | InternalLink>;
    item2Title: string;
    item2Description: string;
    item2Link?: Entry<ExternalLink | InternalLink>;
    item3Title: string;
    item3Description: string;
    item3Link?: Entry<ExternalLink | InternalLink>;
    item4Title: string;
    item4Description: string;
    item4Link?: Entry<ExternalLink | InternalLink>;
  }>;

export type Footer = HasInternalName &
  HasBackgroundColor &
  Field<{
    primaryNavItems: Entries<LinkSet>;
    primaryBackgroundColor: string;
    primaryBackgroundImage: Asset;
    primaryBackgroundSmallImage: Asset;
    primaryFontSize: number;
    primaryMobileFontSize: number;
    primaryFontColor: string;
    secondaryNavItems: Entries<LinkSet>;
    secondaryFontSize: number;
    secondaryFontColor: string;
    socialLinks: Entries<ExternalLink>;
    legalLinks: Entries<ExternalLink | InternalLink>;
    legalFontSize: number;
    legalFontColor: string;
    localePickerFontSize: number;
    localePickerFontColor: string;
  }>;

export type LegalDisclaimer = HasInternalName &
  Field<{
    disclaimerText: string;
    pages: Entries<Page>;
  }>;

export enum FormFields {
  FIRST_AND_LAST_NAME = 'First and Last Name',
  EMAIL = 'Email Address',
  BUSINESS_NAME = 'Business Name',
  PHONE_NUMBER = 'Phone Number',
  MOBILE_PHONE_NUMBER = 'Mobile Phone Number',
  ZIP_CODE = 'ZIP Code',
  BUSINESS_ADDRESS = 'Business Address',
  BUSINESS_TYPE = 'Business Type',
  YEARS_IN_BUSINESS = 'Years in Business',
  SSN_LAST_4 = 'SSN Last 4',
  BANK_ACCOUNT_LAST_4 = 'Bank Account Last 4',
  MID = 'MID',
  DISPLAY_TEXT = 'Display Text',
  GOOGLE_RECAPTCHA = 'Google reCAPTCHA',
  REQUIRED_CHECKBOX = 'Required Checkbox',
  ANNUAL_PROCESSING_VOLUME = 'Annual Processing Volume',
  WHEN_TO_CALL = 'When to Call',
  HIDDEN_FIELD = 'Hidden Field',
}

export type PredefinedFormField = HasKey &
  Field<{
    productionValue: string;
    developmentValue: string;
  }>;

export type FormField = Field<{
  fieldType: FormFields;
  options?: any;
  displayDetail?: ReferencedEntry<Subject>;
  predefinedField?: ReferencedEntry<PredefinedFormField>;
}>;

// ******** assembly content types ******** //

export type Page = Set<Assembly> &
  HasTitle &
  HasSlug &
  HasBackgroundColor &
  HasDescription &
  Field<{
    pageMetadata: ReferencedEntry<any>;
    verticalMarket?: ReferencedEntry<VerticalMarket>;
    heroTopic: ReferencedEntry<Subject>;
    sidebar: ReferencedEntry<Navigation>;
    hideCartIcon?: boolean;
    bannerHidden?: boolean;
    header: ReferencedEntry<Navigation>;
    headerUtil: ReferencedEntry<Navigation>;
    canonicalTag?: string;
    alternatingModuleColor?: string;
    referralId?: string;
    referralIdDev?: string;
    hrefLangTagList?: Document;
    hrefLangLinks?: ReadonlyArray<string>;
    hrefLangXDefaultLink?: string;
    transparentNavBar?: boolean;
    includeSheetModal?: boolean;
  }>;

export type Set<T = Topic> = HasInternalName &
  Field<{
    items: Entries<T>;
  }>;

export type AccordionAssembly = HasComponentMetadata &
  Set &
  HasBackgroundColor &
  HasTitle &
  HasTextAlignment &
  Field<{
    containsImages: boolean;
    alwaysHaveOneItemOpen: boolean;
    hero: ReferencedEntry<Topic>;
  }>;

export type BundleSummaryModule = HasComponentMetadata &
  HasInternalName &
  HasTitle &
  HasSubTitle &
  HasLink &
  HasBackgroundColor &
  Field<{
    cardsArrangement: CardsArrangement;
    solution: ReferencedEntry<Solution>;
    showMoreCTA: string;
    showLessCTA: string;
    showCTAFontSize: number;
    bundleTitleFontSize: number;
    bundleTitleFontColor: string;
    bundlePricingTitleFontSize: number;
    bundlePricingTitleFontColor: string;
    bundlePricingSubTitleFontSize: number;
    bundlePricingSubTitleFontColor: string;
    bundleDescriptionFontSize: number;
    bundleDescriptionFontColor: string;
    hasHybridFeaturesLayout: boolean;
    isPlusIconForShowMore: boolean;
    isBundleCheckoutAtBottom: boolean;
    isBundleFeaturesAlwaysOpen: boolean;
  }>;

export type BYOItemProductsOrSoftwarePlans = ((ProductModule | SoftwarePlan) & {
  _sys: HasContentType;
})[];

/**
 * @type itemKey - unique identifier for byo items, similar to an internalName
 */
export type BYOItem = Field<{
  itemKey: string;
  headlineTitle: string;
  secondaryTitle?: string;
  images: ContentImageEntry[];
  blurb?: string;
  moreInfo?: Document;
  isMultipleAllowed: boolean;
  isAddButtonShown: boolean;
  products?: BYOItemProductsOrSoftwarePlans;
  supportedSoftwarePlans?: SoftwarePlan[];
  forceShowForSoftwarePlans?: SoftwarePlan[];
  addItemIcon?: ContentImageEntry;
  selectedItemCustomIcon?: ContentImageEntry;
  features?: FlatEntry<FeatureSection>[];
  selectButtonText: string;
  selectedButtonText: string;
}>;

export enum BYOStepId {
  BUSINESS_OFFERING = 'BUSINESS_OFFERING',
  POS_NEEDS = 'POS_NEEDS',
  DEVICES = 'DEVICES',
  ACCESSORIES = 'ACCESSORIES',
  ALL_COMPATIBLE_ACCESSORIES = 'ALL_COMPATIBLE_ACCESSORIES',
}

export enum BYOVariants {
  STANDALONE = 'Standalone',
  TABS = 'Tabs',
}

export type BYOStep = Field<{
  queryValue: BYOStepId;
  headline: string;
  subHeadline?: string;
  bypassText?: string;
  minimumSelections: number;
  maximumSelections?: number;
  nextButtonPrimaryText: string;
  nextButtonSecondaryText?: string;
  expandItemText: string;
  collapseItemText: string;
  isSecondaryTitleHidden: boolean;
  autoExpandItems: boolean;
  items: BYOItem[];
}>;

export type BuildYourOwnModule = HasInternalName &
  Field<{
    variant: BYOVariants;
    addItemText: string;
    addItemIcon?: ContentImageEntry;
    decrementItemText: string;
    decrementItemIcon?: ContentImageEntry;
    selectedItemText: string;
    selectedItemCustomIcon?: ContentImageEntry;
    systemReviewTitle: string;
    systemReviewRestartText: string;
    systemReviewEmptyText: string;
    steps: BYOStep[];
    disclaimer: string;
    pricingDisclaimerText: string;
  }>;

export type MediaAssembly = HasComponentMetadata &
  HasInternalName &
  HasBackgroundColor &
  HasTextAlignment &
  HasModuleType &
  HasHeroType &
  Field<{
    topic: ReferencedEntry<Topic>;
    backgroundImage?: ReferencedEntry<Subject>;
    fontWeight?: FontWeight;
    bottomMargin?: ContentfulMargin;
    topPadding?: ContentfulPadding;
    bottomPadding?: ContentfulPadding;
  }>;

export type ListGalleryModule = HasComponentMetadata &
  HasInternalName &
  HasTitle &
  HasTextAlignment &
  Field<{
    variant: ListGalleryVariant;
    items: Entries<Subject>;
  }>;

export type PillTabs = HasInternalName &
  HasTitle &
  HasLink &
  Field<{
    items: Entries<ExternalLink>;
  }>;

export type PlainTextAssembly = HasComponentMetadata &
  HasInternalName &
  HasBackgroundColor &
  Field<{
    topic: ReferencedEntry<Topic>;
  }>;

export type LeadGenFormAssembly = HasComponentMetadata &
  HasInternalName &
  HasTextAlignment &
  Field<{
    topic: ReferencedEntry<Topic>;
    postSubmissionTopic: ReferencedEntry<Topic>;
    formFields?: ReadonlyArray<ReferencedEntry<FormField>>;
    endpoint?: FormEndpointType;
  }>;

export enum ModalType {
  MediaOnly = 'media only',
  PartnerContactForm = 'partner contact form',
  Text = 'text',
  Sheet = 'sheet',
  Lightbox = 'lightbox',
}

export type ModalAssembly = HasInternalName &
  HasSlug &
  Field<{
    type: ModalType;
    topic?: ReferencedEntry<Topic>;
    sheet?: ReferencedEntry<SheetAssembly>;
  }>;

export type FaqModule = HasComponentMetadata &
  HasInternalName &
  HasTitle &
  HasBackgroundColor &
  Field<{
    items: Entries<Faq>;
    variant?: string;
    viewMoreLabel?: string;
    viewLessLabel?: string;
    limitEntryCount?: number;
  }>;

export type FloatingTextAssembly = HasComponentMetadata &
  HasInternalName &
  HasCtaButtonOption &
  HasTextBoxAlignment &
  Field<{
    topic: ReferencedEntry<Topic>;
  }>;

export type CarouselAssembly = HasComponentMetadata &
  Set<Topic> &
  HasTitle &
  HasBackgroundColor &
  HasTextAlignment &
  HasCarouselType &
  Field<{
    autoPlay?: boolean;
  }>;

export type BannerAssembly = HasInternalName &
  Field<{
    topic: ReferencedEntry<Topic>;
  }>;

export type CardAssembly = HasComponentMetadata &
  Set<Subject> &
  HasTitle &
  HasCtaButtonOption &
  Field<{
    isFloating: boolean; // If true then the top of cards will be aligned. If false then the cards will be offset.
    cardBackgroundColor: string;
    variation?: CardModuleVariations;
  }>;

export type ControlledScrollAssembly = HasComponentMetadata &
  HasInternalName &
  HasBackgroundColor &
  HasTitle &
  Field<{
    items: Entries<ControlledScrollItemAssembly>;
  }>;

export type ControlledScrollItemAssembly = HasInternalName &
  HasBackgroundColor &
  Field<{
    isMediaMasked: boolean;
    topic: ReferencedEntry<Topic>;
  }>;

export type GridAssembly = HasComponentMetadata &
  Set<Subject> &
  HasTitle &
  HasBackgroundColor &
  Field<{
    topic: ReferencedEntry<Topic>;
  }>;

export type MultiPanelModule = HasComponentMetadata &
  Set<Subject> &
  HasTitle &
  HasBackgroundColor &
  Field<{
    topic: ReferencedEntry<Topic>;
  }>;

export type TabsAssembly = HasComponentMetadata & Set<TabItem> & HasTitle & HasBackgroundColor;
export type SheetAssemblyItem = ReferencedEntry<
  | SystemDiagram
  | TestimonialAssembly
  | TabsAssembly
  | TextMediaModuleType
  | MediaAssembly
  | CardAssembly
  | AccordionAssembly
  | GridAssembly
  | FaqModule
>;
export type SheetAssembly = HasInternalName &
  HasComponentMetadata &
  HasTitle &
  HasSlug &
  HasCustomIcon &
  Field<{
    items: SheetAssemblyItem[];
  }>;

export type ClientConfig = Field<{
  name: string;
  cookieConfig: CookieConfig[];
}>;

export type LinkSet = Set<Link> &
  HasTitle &
  Field<{
    headerTopic: ReferencedEntry<Subject>;
    primaryLink: ReferencedEntry<Link>;
  }>;

export type NavigationItem = LinkSet | Link;

export type Navigation = Set<NavigationItem> &
  HasTitle &
  HasBackgroundColor &
  Field<{
    type: NavigationType;
    primaryLink: ReferencedEntry<Link>;
    interactionOnHover: boolean;
  }>;

export type Reseller = Field<{
  name: string;
  resellerId: string;
  referrerDomains: string[];
  phoneNumber: string;
  isLiveChatEnabled: boolean;
  redirectUrl?: string;
  brandingBanner?: ReferencedEntry<Subject>;
  legalDisclosureFooterBlurb?: string;
}>;

export type QuotesAssembly = HasBackgroundColor &
  HasColor &
  Field<{
    quote: string;
    merchantName: string;
    businessName: string;
  }>;

export type TestimonialAssembly = HasInternalName &
  HasBackgroundColor &
  HasTextBoxAlignment &
  HasColor &
  HasSecondaryFontColor &
  HasCarouselType &
  Field<{
    testimony: ReferencedEntry<Subject>;
    merchant: ReferencedEntry<Subject>;
    mediaTopic: ReferencedEntry<Subject>;
    stats: ReferencedEntry<Subject>;
    items: Entries<Subject>;
    testimonialType: TestimonialType;
  }>;

export type ArticleAssembly = HasComponentMetadata &
  HasInternalName &
  Field<{
    topic: ReferencedEntry<Topic>;
  }>;

export type ProductTileAssembly = HasComponentMetadata &
  HasInternalName &
  Set<Subject> &
  HasTitle &
  Field<{
    styles?: unknown;
  }>;

export enum PlanIdKeys {
  PREPROD = 'PREPROD',
  PROD = 'PROD',
}

export type MediaFields = HasInternalName &
  Field<{
    source?: string;
    sourceType: MediaSourceType;
  }>;

export type PricingDetailModule = HasComponentMetadata &
  HasInternalName &
  HasTitle &
  HasSubTitle &
  HasBackgroundColor &
  Field<{
    backLink: ReferencedEntry<Link>;
    contactText: string;
    phoneLink: ReferencedEntry<ExternalLink>;
    heroImage: Asset;
    solution: ReferencedEntry<Solution>;
  }>;

export type FeatureFlag = HasKey &
  Field<{
    production: boolean;
    staging: boolean;
    development: boolean;
    localhost: boolean;
  }>;

export type SalesContactModule = HasComponentMetadata &
  HasInternalName &
  HasTitle &
  HasBlurb &
  HasImage &
  HasBackgroundColor &
  Field<{
    imageAlignment: ImageAlignment;
    imageMobileAlignment: ImageMobileAlignment;
    phoneTitle: string;
    phoneLink: ReferencedEntry<ExternalLink>;
    contactLink: ReferencedEntry<ExternalLink>;
  }>;

export type BundleFeatureCategory = HasInternalName & HasTitle & HasSlug;

export type BundleFeature = HasInternalName &
  HasTitle &
  HasSlug &
  HasImage &
  HasDescription &
  Field<{
    bundleFeatureCategory: ReferencedEntry<BundleFeatureCategory>;
    product: ReferencedEntry<ProductModule>;
    variant?: string;
    link: ReferencedEntry<Link>;
  }>;

export type BundleFeatureValue = HasInternalName &
  HasImage &
  Field<{
    variant: BundleFeatureValueVariant;
    included: boolean;
    text: string;
    shortText: string;
    bundleFeature: ReferencedEntry<BundleFeature>;
    product: ReferencedEntry<ProductModule>;
    link: ReferencedEntry<Link>;
  }>;

export type BundleSummaryFeatureValue = HasInternalName & HasImage & HasDescription;

export type Bundle = HasInternalName &
  HasTitle &
  HasSlug &
  HasImage &
  HasLink &
  HasDescription &
  HasDcsId &
  Field<{
    featureValues: Entries<BundleFeatureValue>;
    bundleSummaryImage: Asset;
    shouldUseCheckoutLink: boolean;
    bundleCtaText: string;
    checkoutLink: ReferencedEntry<ExternalLink>;
    bundleSummaryFeatureValues: Entries<BundleSummaryFeatureValue>;
    pricingPageLink: ReferencedEntry<ExternalLink | InternalLink>;
    productDcsId: string;
    softwarePlanDcsId: string;
    contactSalesLink: ReferencedEntry<ExternalLink | InternalLink>;
  }>;

export type Solution = HasInternalName &
  HasTitle &
  Field<{
    bundles: Entries<Bundle>;
    showTag: boolean;
    tagText: string;
    tagBackgroundColor: string;
    bundleWithTag: ReferencedEntry<Bundle>;
  }>;

export type SolutionPickerModule = HasComponentMetadata &
  HasInternalName &
  HasTitle &
  HasSubTitle &
  HasBackgroundColor &
  Field<{
    items: Entries<Subject>;
    isUsedWithEmptyCartPage: boolean;
    variant: string;
  }>;

export type ProductModule = HasInternalName &
  HasDescription &
  HasBackgroundColor &
  HasImages &
  HasDcsId &
  Field<{
    name: string;
    label?: string;
    productFeatures: Entries<ProductFeature>;
    showPrice: boolean;
  }>;

export type ProductFeature = HasInternalName &
  HasTitle &
  Field<{
    value: string;
  }>;

export type StickyHeaderAssembly = HasInternalName &
  HasTitle &
  HasBackgroundColor &
  Field<{
    items: Entries<Product>;
  }>;

export type SideBySideAssembly = CarouselAssembly;

export type ProductSystemsItems = HasInternalName &
  HasDescription &
  HasBackgroundColor &
  HasLink &
  Field<{
    heading: string;
    headingColor: string;
    descriptionColor: string;
    dcsId: string;
    backgroundImage: Asset;
    backgroundColorContainer: string;
    imageAlignment: ImageAlignment;
  }>;

export type ProductSystemsCatalog = HasComponentMetadata &
  HasInternalName &
  HasTitle &
  HasBackgroundColor &
  Field<{
    productSystemItems: Entries<ProductSystemsItems>;
  }>;

export type SoftwarePlan = HasInternalName &
  HasDcsId &
  HasImage &
  HasModalFeatures &
  Field<{
    name: string;
  }>;

export type CloverCareBonusSection = HasInternalName &
  HasModalFeatures &
  HasBlurb &
  HasImage &
  HasTitle &
  Field<{
    redirectUrl: ReferencedEntry<ExternalLink>;
  }>;

export type PromoBanner = HasInternalName &
  HasTitle &
  HasBlurb &
  Field<{
    cta: ReferencedEntry<ExternalLink | InternalLink>;
    enableCustomPromoCopy: boolean;
    hidePromoFromPages: string[];
  }>;

export type PromoSummary = HasInternalName &
  HasTitle &
  HasBlurb &
  Field<{
    enableCustomPromoCopy: boolean;
  }>;

export type HeroModule = HasComponentMetadata &
  Field<{
    item: ReferencedEntry<Subject>;
  }>;

export type PaymentsComparisonModalV2 = HasInternalName &
  HasTitle &
  Field<{
    payInFullToggleText: string;
    payMonthlyToggleText: string;
    compareLinkText: string;
    monthlyOptionHeading: string;
    upfrontOptionHeading: string;
    sideHeadingOptionItems: FlatEntry<Subject>[];
    monthlyOptionItems: FlatEntry<Subject>[];
    upfrontOptionItems: FlatEntry<Subject>[];
    disclaimerItems: FlatEntry<Subject>[];
  }>;

export type ContentImageEntry = FlatSysV2 &
  Partial<{
    title: string;
    file: {
      url: string;
      details?: {
        image?: {
          width: number;
          height: number;
        };
      };
    };
  }>;

export type DevicesMenuItem = HasInternalName &
  HasTitle &
  Field<{
    items: Entries<Subject>;
  }>;

export type EarlyCancellationTerms = HasInternalName &
  Field<{
    earlyCancellationTermsBlurb?: ReferencedEntry<Subject>;
  }>;

export type FeatureGardenModule = HasInternalName &
  Field<{
    paragraph?: Document;
  }>;

export type Feature = HasInternalName &
  Field<{
    icon?: boolean;
    blurb: string;
    underlined: boolean;
    functionalityIncludedText?: string;
    functionalityNotIncludedText?: string;
  }>;

export type FeatureSection = HasInternalName &
  HasTitle &
  Field<{
    includedFeatures: FlatEntry<Feature>[];
  }>;

export type Bullet = HasInternalName &
  Field<{
    icon?: Asset;
    noMarginBottom?: boolean;
    underline?: boolean;
    variant?: 'square' | 'dash';
  }>;
